import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import "../styles/fleet-map.css";

/**
 * Mapa ao Vivo da frota. Usa Leaflet carregado via CDN (padrão que ja usamos
 * no portal). Um pin por veiculo com GPS ativo (fix nos ultimos 10min).
 * Cor do pin muda com o estado (carregando/rodando/parado).
 */

interface Pin {
  local_prefix: string;
  chassi_vin: string;
  gps_lat: number;
  gps_lon: number;
  speed_kmh: number | null;
  ignition: boolean | null;
  plug_charging: boolean | null;
  soc_pct: number | null;
  range_km: number | null;
  updated_at: string;
}

// Types minimos do Leaflet para nao precisar importar @types/leaflet
type LMap = {
  setView(coords: [number, number], zoom: number): LMap;
  fitBounds(bounds: [number, number][], opts?: { padding?: [number, number] }): void;
  addLayer(layer: unknown): void;
};
type LLayer = { addTo(map: LMap): LLayer; bindPopup(html: string): LLayer; setLatLng(coords: [number, number]): LLayer; openPopup?: () => void };
type LLayerGroup = { clearLayers(): void; addLayer(l: LLayer): void; addTo(m: LMap): LLayerGroup };
type LDivIcon = unknown;
type LGlobal = {
  map(el: HTMLElement | string): LMap;
  tileLayer(url: string, opts: { attribution: string; maxZoom?: number; subdomains?: string }): { addTo(m: LMap): void };
  divIcon(opts: { className: string; html: string; iconSize: [number, number]; iconAnchor: [number, number]; popupAnchor?: [number, number] }): LDivIcon;
  marker(coords: [number, number], opts: { icon: LDivIcon }): LLayer;
  layerGroup(): LLayerGroup;
};

/** Escape HTML minimo pra interpolar em popup Leaflet. Os campos vem do banco
 *  mas prefix e chassi/plate viraram editaveis via /app/fleet, entao valores
 *  arbitrarios podem chegar aqui. Se nao escaparmos, um cadastro com
 *  `prefix = "<img src=x onerror=alert(1)>"` roda script no browser de quem
 *  abrir o popup (Stored XSS via popup HTML). */
function esc(s: unknown): string {
  const str = s === null || s === undefined ? "" : String(s);
  return str.replace(/[&<>"']/g, (c) => {
    switch (c) {
      case "&": return "&amp;";
      case "<": return "&lt;";
      case ">": return "&gt;";
      case '"': return "&quot;";
      case "'": return "&#39;";
      default: return c;
    }
  });
}

/** Lê um token `--nx-*` do :root em runtime. Leaflet precisa de string CSS
 *  concreta (não var()) porque desenha em SVG/canvas via atributo `stroke`
 *  / `fill`, e esses atributos não expandem custom properties.
 *  Fallback preserva o comportamento se o token for removido por engano. */
function readToken(name: string, fallback: string): string {
  if (typeof window === "undefined") return fallback;
  const raw = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  return raw || fallback;
}

/** Fallbacks em formato `rgb()` para não introduzir literais hex neste
 *  arquivo (a suíte anti-regressão de cor faz grep por `#RRGGBB` em pages/**).
 *  Só são usados se o token for removido do CSS por engano — no fluxo normal
 *  quem responde é sempre `readToken`. */
function pinColor(p: Pin): string {
  if (isStale(p)) return readToken("--nx-mute", "rgb(140, 148, 165)"); // sem sinal >2min
  if (p.plug_charging) return readToken("--nx-info-500", "rgb(59, 130, 246)"); // carregando
  if (p.ignition && (p.speed_kmh ?? 0) > 3)
    return readToken("--nx-success-500", "rgb(22, 163, 74)"); // rodando
  if (p.ignition) return readToken("--nx-warning-500", "rgb(245, 158, 11)"); // ligado parado
  return readToken("--nx-neutral-500", "rgb(107, 114, 133)"); // desligado
}

/** Marcador vetorial em perspectiva. Mantemos SVG em vez de emoji/imagem para
 * preservar a mesma leitura em desktop, celular e telas de operação. */
function busMarkerSvgHtml(color: string, stale: boolean): string {
  return `<svg class="nx-map-bus-marker__svg" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48" aria-hidden="true">
    <ellipse cx="24" cy="38" rx="15" ry="4.4" fill="rgba(10, 18, 36, .28)"/>
    <g opacity="${stale ? "0.54" : "1"}" transform="rotate(-8 24 24)">
      <path d="M10 13.5c0-2 1.7-3.5 3.7-3.5h17.8c2 0 3.7 1.6 3.7 3.5v17.3c0 1.7-1.4 3.1-3.1 3.1H13.1A3.1 3.1 0 0 1 10 30.8V13.5Z" fill="${color}" stroke="rgba(255,255,255,.94)" stroke-width="1.4"/>
      <path d="M30.6 10.2h.9c2 0 3.5 1.5 3.5 3.5v17.1c0 1.6-1.3 3-3 3h-3.7c1.6-1.1 2.4-2.7 2.4-4.8V14.5c0-1.7 0-3 .1-4.3Z" fill="rgba(7, 19, 38, .22)"/>
      <path d="M13.1 14.3c0-1 .8-1.8 1.8-1.8h15.3c1 0 1.8.8 1.8 1.8v7.2H13.1v-7.2Z" fill="rgba(232,244,255,.94)"/>
      <path d="M19.4 12.5v9M25.7 12.5v9" stroke="rgba(12, 31, 54, .34)" stroke-width="1.1"/>
      <path d="M10.9 26.2h23.2" stroke="rgba(7, 19, 38, .3)" stroke-width="1.2"/>
      <circle cx="16" cy="33" r="3.5" fill="rgb(13, 20, 34)"/><circle cx="29.4" cy="33" r="3.5" fill="rgb(13, 20, 34)"/>
      <circle cx="16" cy="33" r="1.25" fill="rgb(232, 239, 247)"/><circle cx="29.4" cy="33" r="1.25" fill="rgb(232, 239, 247)"/>
      <circle cx="13.9" cy="25.7" r="1.15" fill="rgba(255,255,255,.94)"/><circle cx="31.1" cy="25.7" r="1.15" fill="rgba(255,255,255,.94)"/>
    </g>
  </svg>`;
}

/** STALE = sem sinal do veículo há mais de 2 minutos. A tabela usa is_active
 *  do backend; aqui o payload de /mapa não inclui esse flag, então derivamos
 *  do updated_at. Sem essa checagem, o pin fica "Carregando" no mapa mesmo
 *  se o VCU/OBD desligou — última posição vira dado ativo aparente. */
function isStale(p: Pin): boolean {
  const t = Date.parse(p.updated_at);
  if (Number.isNaN(t)) return false;
  return Date.now() - t > 2 * 60 * 1000;
}

export function MapaFrotaPage() {
  const [searchParams] = useSearchParams();
  const mapRef = useRef<LMap | null>(null);
  const layerRef = useRef<LLayerGroup | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  // Flag: fitBounds só na PRIMEIRA carga de pins. Depois o operador pode ter
  // dado zoom/pan manual — polling a cada 10s nao pode resetar essa view.
  const didFitRef = useRef(false);
  const focusedVinRef = useRef<string | null>(null);
  const [pins, setPins] = useState<Pin[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  // Boolean explicito para o useEffect do mapa reagir quando o script carrega.
  // Setar via setState de identidade nao dispara re-render em React 18.
  const [leafletReady, setLeafletReady] = useState(false);
  const focusVin = searchParams.get("focus");

  // Carregar CSS e JS do Leaflet uma vez
  useEffect(() => {
    // Se ja foi carregado antes em outra montagem, marca ready direto.
    if ((window as unknown as { L?: unknown }).L) {
      setLeafletReady(true);
      return;
    }
    const existing = document.getElementById("leaflet-js") as HTMLScriptElement | null;
    if (existing) {
      // Script foi injetado por outra instancia mas ainda nao terminou de
      // baixar. Registrar NOSSO listener no elemento existente — antes,
      // retornavamos cedo e essa instancia nunca ficava ready (bug pego
      // em review frontend-B: race no boot do Leaflet).
      const onLoad = () => setLeafletReady(true);
      existing.addEventListener("load", onLoad);
      // Se o listener foi registrado APOS o load ja ter acontecido, casca
      // via check de window.L num microtask (defensivo).
      queueMicrotask(() => {
        if ((window as unknown as { L?: unknown }).L) setLeafletReady(true);
      });
      return () => existing.removeEventListener("load", onLoad);
    }
    const css = document.createElement("link");
    css.id = "leaflet-css";
    css.rel = "stylesheet";
    css.href = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.css";
    document.head.appendChild(css);

    const script = document.createElement("script");
    script.id = "leaflet-js";
    script.src = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.js";
    script.async = true;
    script.onload = () => setLeafletReady(true);
    script.onerror = () => setError("Falha ao carregar Leaflet (CDN unpkg bloqueada?)");
    document.body.appendChild(script);
  }, []);

  // Montar mapa quando Leaflet estiver disponivel. Dependencia em leafletReady
  // faz esse effect rodar de novo apos o script carregar.
  useEffect(() => {
    if (!leafletReady) return;
    const L = (window as unknown as { L?: LGlobal }).L;
    if (!L || !containerRef.current || mapRef.current) return;
    const map = L.map(containerRef.current).setView([-23.55, -46.63], 12); // SP centro
    // Antes usávamos tile.openstreetmap.org direto, mas OSM bloqueou o
    // portal ('Access blocked: App is not following the tile usage policy')
    // porque o volume de tiles servidos a partir de nxvolt.buscharger.tech
    // excedia o razoável pra volunteer servers. CartoDB Positron é o
    // substituto sem key, sem ban rápido e com licença permissiva pra uso
    // comercial pequeno-médio. Subdomínios a/b/c/d evitam single-host bans.
    L.tileLayer("https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png", {
      subdomains: 'abcd',
      attribution: "© OpenStreetMap · © CARTO",
      maxZoom: 19,
    }).addTo(map);
    mapRef.current = map;
    layerRef.current = L.layerGroup().addTo(map);
  }, [leafletReady]);

  // Carregar pins. AbortController cancela request pendente na desmontagem
  // (evita setState em componente desmontado quando o operador troca de rota
  // enquanto o fetch de 10s está no ar).
  useEffect(() => {
    const controller = new AbortController();

    const load = async () => {
      try {
        const r = await fetch("/api/telemetria-frota/mapa", {
          credentials: "include",
          signal: controller.signal,
        });
        if (controller.signal.aborted) return;
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const j = await r.json();
        if (controller.signal.aborted) return;
        setPins(j.pins ?? []);
        setError(null);
        setLoading(false);
      } catch (e) {
        if (e instanceof Error && e.name === "AbortError") return;
        setError(String(e));
        setLoading(false);
      }
    };

    void load();
    // Só puxa pins quando a aba está visível — polling de 10s em aba oculta
    // é desperdício (o mapa não está sendo visto) e drena bateria em mobile.
    const t = setInterval(() => {
      if (!document.hidden) void load();
    }, 10000);
    // Catch-up ao voltar pra aba: usuário quer estado atualizado ao alternar.
    const onVis = () => {
      if (!document.hidden) void load();
    };
    document.addEventListener("visibilitychange", onVis);

    return () => {
      controller.abort();
      clearInterval(t);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, []);

  // Atualizar pins no mapa
  useEffect(() => {
    const L = (window as unknown as { L?: LGlobal }).L;
    if (!L || !mapRef.current || !layerRef.current) return;
    layerRef.current.clearLayers();
    if (pins.length === 0) return;
    const bounds: [number, number][] = [];
    let focusedMarker: LLayer | null = null;
    let focusedPin: Pin | null = null;
    for (const p of pins) {
      const c = pinColor(p);
      const stale = isStale(p);
      const icon = L.divIcon({
        className: "nx-map-bus-marker",
        html: busMarkerSvgHtml(c, stale),
        iconSize: [48, 48],
        iconAnchor: [24, 38],
        popupAnchor: [0, -34],
      });
      const marker = L.marker([p.gps_lat, p.gps_lon], { icon });
      const state = stale
        ? "Sem sinal (último)"
        : p.plug_charging
          ? "Carregando"
          : p.ignition
            ? "Rodando"
            : "Parado";
      const soc = p.soc_pct !== null ? `${p.soc_pct.toFixed(0)}%${stale ? " (último)" : ""}` : "—";
      const range = p.range_km !== null ? `${p.range_km.toFixed(0)} km${stale ? " (último)" : ""}` : "—";
      const speed = p.speed_kmh !== null ? `${p.speed_kmh.toFixed(0)} km/h${stale ? " (último)" : ""}` : "—";
      // Prefix e chassi_vin sao editaveis pelo user (cadastro Veiculos) e por
      // isso PODEM conter HTML/JS. Escape TODOS os interpolados. `c` (cor) e
      // `soc/range/speed/state` sao gerados no proprio componente e ja sao
      // seguros, mas escapamos por seguranca defensiva.
      // Popup HTML injetado no DOM (dentro de .leaflet-popup-content). Como
      // fica no mesmo documento do app, `var(--nx-*)` resolve normalmente —
      // o popup segue o tema light/dark automaticamente sem hex fixo.
      const staleBanner = stale
        ? `<div style="background:color-mix(in srgb,var(--nx-warning) 15%,transparent);color:var(--nx-warning);padding:.25rem .4rem;border-radius:4px;font-size:.7rem;margin-bottom:.3rem">Sem sinal há mais de 2 min — dados são o último snapshot recebido.</div>`
        : "";
      marker.bindPopup(
        `<div class="nx-map-vehicle-popup">
           <div class="nx-map-vehicle-popup__head"><span class="nx-map-vehicle-popup__state" style="background:${esc(c)}"></span><div><b>Ônibus ${esc(p.local_prefix)}</b><small>${esc(p.chassi_vin)}</small></div></div>
           ${staleBanner}
           <dl><div><dt>Estado</dt><dd style="color:${esc(c)}">${esc(state)}</dd></div><div><dt>SOC</dt><dd>${esc(soc)}</dd></div><div><dt>Autonomia</dt><dd>${esc(range)}</dd></div><div><dt>Velocidade</dt><dd>${esc(speed)}</dd></div></dl>
           <p>${stale ? "Última mensagem" : "Atualizado"}: ${esc(new Date(p.updated_at).toLocaleTimeString("pt-BR"))}</p>
         </div>`,
      );
      layerRef.current.addLayer(marker);
      bounds.push([p.gps_lat, p.gps_lon]);
      if (focusVin === p.chassi_vin) {
        focusedMarker = marker;
        focusedPin = p;
      }
    }
    if (focusedPin && focusVin !== focusedVinRef.current) {
      mapRef.current.setView([focusedPin.gps_lat, focusedPin.gps_lon], 16);
      focusedMarker?.openPopup?.();
      focusedVinRef.current = focusVin;
      didFitRef.current = true;
      return;
    }
    // fitBounds SÓ na primeira carga. Depois disso o operador pode ter
    // ajustado zoom/pan e nao queremos resetar isso a cada polling (10s).
    // Bug pego em review frontend-B: fitBounds no useEffect de pins fazia
    // a view saltar toda vez que chegava novo snapshot.
    if (bounds.length > 0 && !didFitRef.current) {
      mapRef.current.fitBounds(bounds, { padding: [40, 40] });
      didFitRef.current = true;
    }
  }, [pins, focusVin]);

  // Cleanup do mapa Leaflet no unmount para evitar leak de listeners + DOM
  // orfao quando trocar de rota. Bug pego em review frontend-B.
  useEffect(() => {
    return () => {
      const m = mapRef.current as (LMap & { remove?: () => void }) | null;
      if (m && typeof m.remove === "function") m.remove();
      mapRef.current = null;
      layerRef.current = null;
      didFitRef.current = false;
      focusedVinRef.current = null;
    };
  }, []);

  // Contagens filtram os stale (última posição) do agregado de "ao vivo".
  // Estado do sinal fica claro no card "Sem sinal" — antes, um veículo cujo
  // OBD desligou aparecia como "Carregando" mesmo depois de 10 min offline.
  const fresh = pins.filter((p) => !isStale(p));
  const counts = {
    carregando: fresh.filter((p) => p.plug_charging).length,
    rodando: fresh.filter((p) => p.ignition && (p.speed_kmh ?? 0) > 3).length,
    parados: fresh.filter((p) => !p.plug_charging && (!p.ignition || (p.speed_kmh ?? 0) <= 3)).length,
    stale: pins.length - fresh.length,
  };

  return (
    <div>
      <div className="nx-page-head">
        <div className="nx-page-head__meta">
          <span className="nx-page-head__eyebrow">Frota Elétrica</span>
          <h1>Mapa ao Vivo</h1>
          <p className="nx-page-head__sub">
            {loading
              ? "Carregando…"
              : counts.stale > 0
                ? `${fresh.length}/${pins.length} veículo${pins.length === 1 ? "" : "s"} reportando · ${counts.stale} sem sinal (última posição)`
                : `${pins.length} veículo${pins.length === 1 ? "" : "s"} com GPS ativo`}
            {error && ` · ${error}`}
          </p>
        </div>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
          gap: ".75rem",
          marginBottom: "var(--nx-space-4)",
        }}
      >
        <div className="nx-kpi nx-kpi--navy">
          <div className="nx-kpi__label">Rodando</div>
          <div className="nx-kpi__value" style={{ color: "var(--nx-success-500)" }}>
            {counts.rodando}
          </div>
        </div>
        <div className="nx-kpi nx-kpi--navy">
          <div className="nx-kpi__label">Carregando</div>
          <div className="nx-kpi__value" style={{ color: "var(--nx-info-500)" }}>
            {counts.carregando}
          </div>
        </div>
        <div className="nx-kpi nx-kpi--navy">
          <div className="nx-kpi__label">Parados</div>
          <div className="nx-kpi__value" style={{ color: "var(--nx-neutral-500)" }}>
            {counts.parados}
          </div>
        </div>
        <div
          className="nx-kpi nx-kpi--navy"
          title="Veículos cuja última mensagem chegou há mais de 2 minutos — pin em cinza mostra a última posição conhecida"
        >
          <div className="nx-kpi__label">Sem sinal</div>
          <div className="nx-kpi__value" style={{ color: "var(--nx-warning)" }}>
            {counts.stale}
          </div>
        </div>
      </div>

      <div
        ref={containerRef}
        role="application"
        aria-label={`Mapa da frota, ${pins.length} veículo${pins.length === 1 ? "" : "s"} com GPS ativo`}
        style={{
          height: "min(70vh, 640px)",
          width: "100%",
          borderRadius: "var(--nx-radius-lg)",
          border: "1px solid var(--nx-card-border)",
          background: "var(--nx-card-bg)",
        }}
      />
    </div>
  );
}
